import type { CSSObject } from '@emotion/react'
import { CSSProperties } from 'react'
import { Color } from '../Color'
import { DeprecatedGemColors } from '../Color/DeprecatedGemColors'
import { gemVar } from '../Color/utils/gemVar'
import { wideGamutColor } from '../Color/utils/wideGamutColor'
import { ThemedColors } from '../Color/variables'
import { Theme } from '../Theme'
import { type BoxFocus, type SVGFocus } from './types'

const shared: CSSObject = {
  outline: 'none',
} as const

/**
 * @note It’s important that this has contrasting light and dark colors, so that
 * there’s a visible outline regardless of the color(s) behind the focused
 * interface element.
 */
function offsetAndColor(theme: Theme): [number, string][] {
  const accent =
    theme === 'dark' ? DeprecatedGemColors.BD200 : DeprecatedGemColors.BL200

  return [
    [1, Color[theme].Surface.N000],
    [3, wideGamutColor(accent, 0.75)],
  ]
}

/**
 * This is an approximation of using `box-shadow`: `box-shadow` is always drawn
 * at the outside rectangle of an HTML element, but has a `spread` parameter
 * that can create a solid stroke; `filter`’s `drop-shadow` can be an arbitrary
 * shape because it uses the filled part of an element, but can’t create a solid
 * stroke. The thick stroke can be approximated with an SVG `stroke`; there
 * doesn’t seem to be a good way to handle the thin stroke.
 *
 * Things that didn’t work:
 *
 * ```
 * path: {
 *   filter: `drop-shadow(0 0 ${thin[0]} ${thin[1]})`,
 *   outline: `${distance} solid ${color}`,
 * }
 * ```
 *
 * (`outline` applies to the rectangle containing `path`, not to path’s
 * shape)
 *
 * ```
 * filter: [
 *   `drop-shadow(0 0 ${thin[0]} ${thin[1]})`,
 *   `drop-shadow(${distance} 0 0 ${color})`,
 *   `drop-shadow(-${distance} 0 0 ${color})`,
 *   `drop-shadow(0 ${distance} 0 ${color})`,
 *   `drop-shadow(0 -${distance} 0 ${color})`,
 * ].join(' ')
 * ```
 *
 * https://stackoverflow.com/a/75685100
 * (the four offset shadows blend to a color that’s too intense, cause weird
 * blending artifacts, and clip at the edges of the container)
 *
 * ```
 * filter: `drop-shadow(0 0 ${thin[0]}px ${thin[1]})`,
 * // stroke styles
 * ```
 *
 * (the `drop-shadow` draws outside of the `stroke`, which is normally
 * desirable, but in this the thin stroke supposed to be inside the thick
 * stroke)
 */
function svgShadow(theme: Theme): CSSObject {
  const [, [distance, color]] = offsetAndColor(theme)

  return {
    svg: {
      // Prevent stroke from clipping to the edges of the SVG.
      overflow: 'visible',
    },
    path: {
      stroke: color,
      // SVG strokes are centered on the edges of the `path`, so a 2 px stroke
      // draws 1 px on each side of the path boundary. This doesn't match the
      // intention of rendering an outline, but scaling up the `stroke-width`
      // and putting a mask in front of the SVG to hide the inner part of the
      // stroke basically works.
      strokeWidth: distance * 1.75,
    },
  }
}

/**
 * Replace the browser’s default focus indicator with one that’s still
 * accessible, but works better with the Gem Design System.
 *
 * This is similar to `FocusIndicator`, but specialized for handling
 * SVGs that aren’t a filled rectangle.
 *
 * @example
 * '&:focus': {
 *   // ...
 *   ...SVGFocusIndicator[theme],
 * },
 */
export const SVGFocusIndicator: SVGFocus = {
  dark: svgShadow('dark'),
  light: svgShadow('light'),
} as const

function boxShadow(theme: Theme, inset = false): string {
  return offsetAndColor(theme)
    .map(
      ([distance, color]) =>
        `${inset ? 'inset ' : ''}0 0 0 ${distance}px ${color}`
    )
    .join(', ')
}

/**
 * @note Use `ThemedFocusIndicator` instead.
 */
export const FocusIndicator: BoxFocus = {
  dark: {
    boxShadow: boxShadow('dark'),
    ...shared,
  },
  light: {
    boxShadow: boxShadow('light'),
    ...shared,
  },
} as const

/**
 * The same indicator as `FocusIndicator`, drawn inside the element’s border
 * box instead of outside it. Use it for elements packed edge to edge, such as
 * table cells. An outward ring overlaps the neighboring elements, and a
 * neighbor in its own stacking context (anything with a `transform`) paints
 * below the ring whatever its `z-index` is.
 *
 * @note An `outline` is also drawn outside the border box. An element that has
 * one too needs `outline-offset: -1px` to keep it inside.
 */
export const InsetFocusIndicator: BoxFocus = {
  dark: {
    boxShadow: boxShadow('dark', true),
    ...shared,
  },
  light: {
    boxShadow: boxShadow('light', true),
    ...shared,
  },
} as const

/**
 * How far `ThemedFocusIndicator` draws outside an element’s border box, in
 * pixels.
 *
 * @note `box-shadow` never counts as scrollable overflow, so a scroll
 * container with focusable children has to reserve this much room for the
 * indicator or it will be clipped.
 */
export const FOCUS_RING_WIDTH = 3

/**
 * Replace the browser’s default focus indicator with one that’s still
 * accessible, but works better with the Gem Design System.
 *
 * @note Use this in combination with `Transition`, adding `box-shadow` to the
 * `properties` array.
 * @note For SVGs you may want `SVGFocusIndicator`.
 *
 * @example
 * '&:focus-visible': {
 *   ...FocusIndicator,
 * },
 *
 * @example
 * '&:focus-visible': {
 *   boxShadow: [
 *     FocusIndicator.boxShadow,
 *     Shadow[ShadowElevation.E1].boxShadow,
 *   ].join(', '),
 *   outline: 'none',
 * },
 */
export const ThemedFocusIndicator = {
  boxShadow: `0 0 0 1px ${gemVar(ThemedColors.Surface1)}, 0 0 0 ${FOCUS_RING_WIDTH}px ${gemVar(ThemedColors.StrokePrimary2)}`,
  outline: 'none',
} as const satisfies CSSProperties
